import apiClient from "./api";
import type {
  LoginCredentials,
  RegisterData,
  User,
  ApiResponse,
  ApiError,
} from "../types/auth";


class AuthService {
  private readonly endpoints = {
    login: "/api/v1/auth/login",
    logout: "/api/v1/auth/logout",
    register: "/api/v1/auth/register",
    me: "/api/v1/auth/me",
  } as const;


  async login(credentials: LoginCredentials): Promise<User> {
    try {
      const response = await apiClient.post<{
        user: User;
        has_proponente?: boolean;
        message: string;
      }>(this.endpoints.login, credentials);

      // Backend Rails retorna: { user: {...}, has_proponente: true/false }
      const userData = response.data.user;

      if (!userData) {
        throw new Error("Dados de usuário não recebidos do servidor");
      }

      if (typeof response.data.has_proponente !== 'undefined') {
        // attach property to user object for frontend use
        (userData as User).has_proponente = response.data.has_proponente;
      }

      return userData;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: ApiError; status?: number } };
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;
      
      // Mensagens específicas para erros de login
      let message = "Erro ao fazer login";
      
      if (errorData?.error) {
        message = errorData.error;
        
        // Melhora mensagens comuns de login
        if (message.includes("Email ou senha inválidos") || 
            message.includes("Invalid email or password") ||
            message.includes("Unauthorized")) {
          message = "Email ou senha incorretos";
        }
      } else if (status === 401) {
        message = "Email ou senha incorretos";
      } else if (status === 422) {
        message = "Dados inválidos. Verifique email e senha";
      } else if (status === 500) {
        message = "Erro interno do servidor. Tente novamente";
      }
      
      throw new Error(message);
    }
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post(this.endpoints.logout);
    } catch (error) {
      // Logout sempre deve funcionar localmente
      // Log do erro mas não bloqueia o processo
      console.warn("Aviso no logout:", error);
    }
  }

  async register(userData: RegisterData): Promise<void> {
    try {
      await apiClient.post<ApiResponse>(this.endpoints.register, userData);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: ApiError } };
      const errorData = axiosError.response?.data;

      if (errorData?.errors) {
        // Erros de validação do Rails
        let errorMessages: string;
        
        if (Array.isArray(errorData.errors)) {
          errorMessages = errorData.errors.join(", ");
        } else if (errorData.details) {
          const detailMessages = Object.entries(errorData.details)
            .map(([field, messages]) => {
              const fieldName = field === 'email' ? 'Email' : 
                              field === 'cpf' ? 'CPF' : 
                              field === 'nome' ? 'Nome' : field;
              return `${fieldName}: ${Array.isArray(messages) ? messages.join(', ') : messages}`;
            });
          errorMessages = detailMessages.join("; ");
        } else {
          errorMessages = "Erro de validação";
        }
        
        throw new Error(errorMessages);
      }

      const message = errorData?.error || "Erro ao registrar usuário";
      throw new Error(message);
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get<{ user: User; has_proponente?: boolean }>(
        this.endpoints.me
      );

      const userData = response.data.user;

      if (!userData) {
        throw new Error("Usuário não encontrado na sessão");
      }

      if (typeof response.data.has_proponente !== 'undefined') {
        (userData as User).has_proponente = response.data.has_proponente;
      }

      return userData;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: ApiError; status?: number } };
      const message = axiosError.response?.data?.error || "Sessão inválida";
      throw new Error(message);
    }
  }

  /**
   * Verifica se API está disponível
   * @returns true se API responder corretamente
   */
  async healthCheck(): Promise<boolean> {
    try {
      await apiClient.get("/up"); // Endpoint padrão do Rails
      return true;
    } catch (error) {
      console.warn("API não disponível:", error);
      return false;
    }
  }

  isAdmin(user: User | null): boolean {
    return user?.tipo_usuario === "admin";
  }

  isProponente(user: User | null): boolean {
    return user?.tipo_usuario === "proponente";
  }

  getHomeRoute(user: User): string {
    return user.tipo_usuario === "admin" ? "/admin/home" : "/home";
  }
}

export const authService = new AuthService();
export default authService;
